import {
  type ProspectContactDto,
  type SalesEmailTemplateDto,
  useSalesGetSalesEmailTemplates,
  useSalesSendSalesTemplateEmail,
} from "@crowdcoursing/api/openapi";
import { Close as CloseIcon, Send } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { useToast } from "~hooks/useToast";
import { isEmptySalesEmailBody, SalesEmailBodyEditor } from "~ui/molecules/SalesEmailBodyEditor";

const tClose = "Close";
const tSend = "Send";
const tSending = "Sending…";
const tTo = "To";
const tTemplate = "Template";
const tNewEmail = "New Email";
const tGreeting = "Greeting";
const tSubject = "Subject";
const tBody = "Body";
const tTitle = "Email contact";
const tSuccess = "Email sent.";
const tMissingFields = "Greeting, subject, and body are required.";

/** Last chosen template id, or `"new"` for a blank email. */
const localStorageKey = "sales.prospectContactEmail.templateChoice";

type TemplateChoice = "new" | string;

function readStoredTemplateChoice(): TemplateChoice {
  try {
    const raw = localStorage.getItem(localStorageKey);
    if (raw === "new") {
      return "new";
    }
    if (raw && /^[0-9a-f-]{36}$/i.test(raw)) {
      return raw;
    }
  } catch {
    // ignore
  }
  return "new";
}

function writeStoredTemplateChoice(choice: TemplateChoice) {
  try {
    localStorage.setItem(localStorageKey, choice);
  } catch {
    // ignore
  }
}

function defaultGreetingForContact(c: ProspectContactDto): string {
  const first = c.firstName?.trim() ?? "";
  const last = c.lastName?.trim() ?? "";
  if (!first && !last) {
    return "Hello!";
  }
  return `Hello ${[first, last].filter(Boolean).join(" ")}!`;
}

export interface ContactEmailSlideoutProps {
  contact: ProspectContactDto;
  onClose: () => void;
}

export function ContactEmailSlideout({ contact, onClose }: ContactEmailSlideoutProps) {
  const toast = useToast();
  const { data: templates = [], isLoading: templatesLoading } = useSalesGetSalesEmailTemplates({});
  const { mutateAsync: sendEmail, isPending: isSending } = useSalesSendSalesTemplateEmail();

  const templatesWithId = useMemo(
    () => (templates ?? []).filter((t): t is SalesEmailTemplateDto & { id: string } => Boolean(t.id)),
    [templates],
  );

  const [templateChoice, setTemplateChoice] = useState<TemplateChoice>(readStoredTemplateChoice);

  // If localStorage points at a deleted template, fall back to "New Email".
  useEffect(() => {
    if (!templatesWithId.length) {
      return;
    }
    setTemplateChoice((current) => {
      if (current === "new") {
        return current;
      }
      if (templatesWithId.some((t) => t.id === current)) {
        return current;
      }
      writeStoredTemplateChoice("new");
      return "new";
    });
  }, [templatesWithId]);

  const [greeting, setGreeting] = useState(() => defaultGreetingForContact(contact));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const recipientEmail = contact.email?.trim() ?? "";
  const contactLabel = useMemo(
    () => [contact.firstName, contact.lastName].filter(Boolean).join(" "),
    [contact.firstName, contact.lastName],
  );

  // Keep subject/body in sync with template list + dropdown selection (templates may load after mount).
  useEffect(() => {
    if (templateChoice === "new") {
      setSubject("");
      setBody("");
      return;
    }
    const tpl = templatesWithId.find((t) => t.id === templateChoice);
    setSubject(tpl?.subject ?? "");
    setBody(tpl?.body ?? "");
  }, [templateChoice, templatesWithId]);

  const handleTemplateChange = (choice: TemplateChoice) => {
    setTemplateChoice(choice);
    writeStoredTemplateChoice(choice);
  };

  const handleSend = async () => {
    const g = greeting.trim();
    const s = subject.trim();
    const b = body.trim();
    if (!g || !s || isEmptySalesEmailBody(b) || !recipientEmail) {
      toast.error(tMissingFields);
      return;
    }
    try {
      await sendEmail({
        body: { body: b, greeting: g, recipientEmail, subject: s },
      });
      toast.success(tSuccess);
      onClose();
    } catch (e) {
      const message =
        e && typeof e === "object" && "payload" in e && typeof (e as { payload: unknown }).payload === "string"
          ? (e as { payload: string }).payload
          : e instanceof Error
            ? e.message
            : "Failed to send email.";
      toast.error(message);
    }
  };

  return (
    <Drawer
      anchor="right"
      aria-label={tTitle}
      onClose={onClose}
      open
      role="complementary"
      sx={{
        "& .MuiDrawer-paper": {
          borderRadius: "16px 0 0 16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          width: { md: 520, sm: "100%", xs: "100%" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            alignItems: "center",
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            display: "flex",
            gap: 1,
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography component="h2" sx={{ pr: 1 }} variant="h6">
            {tTitle}
            {contactLabel ? ` — ${contactLabel}` : ""}
          </Typography>
          <IconButton aria-label={tClose} onClick={onClose} size="small" sx={{ color: "inherit" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack spacing={2} sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <TextField fullWidth InputProps={{ readOnly: true }} label={tTo} value={recipientEmail} variant="outlined" />

          <FormControl fullWidth disabled={templatesLoading}>
            <InputLabel id="contact-email-template-label">{tTemplate}</InputLabel>
            <Select
              id="contact-email-template"
              label={tTemplate}
              labelId="contact-email-template-label"
              onChange={(e) => handleTemplateChange(e.target.value as TemplateChoice)}
              value={templateChoice}
            >
              <MenuItem value="new">{tNewEmail}</MenuItem>
              {templatesWithId.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name ?? "Untitled"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={tSubject}
            minRows={2}
            multiline
            onChange={(e) => setSubject(e.target.value)}
            value={subject}
          />
          <TextField
            fullWidth
            label={tGreeting}
            minRows={2}
            multiline
            onChange={(e) => setGreeting(e.target.value)}
            value={greeting}
          />
          <SalesEmailBodyEditor key={templateChoice} label={tBody} minRows={10} onChange={setBody} value={body} />
        </Stack>

        <Box sx={{ borderColor: "divider", borderTop: 1, display: "flex", gap: 1, justifyContent: "flex-end", p: 2 }}>
          <Button disabled={isSending} onClick={onClose}>
            {tClose}
          </Button>
          <Button
            disabled={isSending || !recipientEmail}
            onClick={() => void handleSend()}
            startIcon={isSending ? <CircularProgress color="inherit" size={18} /> : <Send />}
            variant="contained"
          >
            {isSending ? tSending : tSend}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
